import React from 'react';
import { useTranslation } from 'react-i18next';
import { LegalPageLayout } from './LegalPageLayout';
import { FaGavel } from 'react-icons/fa';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <h2 className="section-title">{children}</h2>;

const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="info-box">{children}</div>
);

const XIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4 text-red-300"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export const Terms: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    { id: 'agreement', title: t('termsOfUse.sections.agreement.title') },
    { id: 'disclaimer', title: t('termsOfUse.sections.disclaimer.title') },
    { id: 'age', title: t('termsOfUse.sections.age.title') },
    { id: 'intellectual', title: t('termsOfUse.sections.intellectual.title') },
    { id: 'liability', title: t('termsOfUse.sections.liability.title') },
    { id: 'changes', title: t('termsOfUse.sections.changes.title') },
  ];

  return (
    <LegalPageLayout
      icon={<FaGavel />}
      title={t('termsOfUse.title')}
      lastUpdated={t('termsOfUse.lastUpdated')}
      contentsLabel={t('termsOfUse.contents')}
      sections={sections}
    >
      {(activeSection) => (
        <div className="animate-fade-in space-y-6">
          {/* Agreement to Terms */}
          {activeSection === 'agreement' && (
            <div>
              <SectionTitle>
                {t('termsOfUse.sections.agreement.title')}
              </SectionTitle>
              <InfoBox>
                <p>{t('termsOfUse.sections.agreement.description')}</p>
              </InfoBox>
            </div>
          )}

          {/* Disclaimer */}
          {activeSection === 'disclaimer' && (
            <div>
              <SectionTitle>
                {t('termsOfUse.sections.disclaimer.title')}
              </SectionTitle>
              <div className="feature-card border-purple-500/30">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-purple-500/20 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-purple-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </span>
                  <h3 className="text-lg font-semibold text-white">
                    {t('termsOfUse.sections.disclaimer.importantInfo')}
                  </h3>
                </div>
                <div className="space-y-3 text-sm text-gray-400">
                  <p>{t('termsOfUse.sections.disclaimer.paragraph1')}</p>
                  <p>{t('termsOfUse.sections.disclaimer.paragraph2')}</p>
                  <p>{t('termsOfUse.sections.disclaimer.paragraph3')}</p>
                </div>
              </div>
              <div className="mt-4 warning-box">
                <h4 className="font-semibold text-yellow-200 text-sm mb-1">
                  {t('termsOfUse.sections.disclaimer.responsibleGambling')}
                </h4>
                <p className="text-xs text-gray-400">
                  {t('termsOfUse.sections.disclaimer.responsibleGamblingDesc')}
                </p>
              </div>
            </div>
          )}

          {/* Age Restriction */}
          {activeSection === 'age' && (
            <div>
              <SectionTitle>{t('termsOfUse.sections.age.title')}</SectionTitle>
              <div className="feature-card">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-red-500/20 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-red-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                      />
                    </svg>
                  </span>
                  <h3 className="text-lg font-semibold text-white">
                    {t('termsOfUse.sections.age.ageLimit')}
                  </h3>
                </div>
                <p className="text-sm text-gray-400">
                  {t('termsOfUse.sections.age.description')}
                </p>
                <div className="mt-4 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg p-4">
                  <p className="text-sm text-red-200 font-semibold">
                    {t('termsOfUse.sections.age.restriction')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Intellectual Property */}
          {activeSection === 'intellectual' && (
            <div>
              <SectionTitle>
                {t('termsOfUse.sections.intellectual.title')}
              </SectionTitle>
              <div className="feature-card">
                <p className="text-sm text-gray-400 mb-4">
                  {t('termsOfUse.sections.intellectual.description')}
                </p>
                <h3 className="text-white font-semibold mb-3">
                  {t('termsOfUse.sections.intellectual.prohibitedUses')}
                </h3>
                <div className="space-y-2">
                  {[
                    t('termsOfUse.sections.intellectual.prohibition1'),
                    t('termsOfUse.sections.intellectual.prohibition2'),
                    t('termsOfUse.sections.intellectual.prohibition3'),
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="bg-red-500/20 p-1 rounded-full flex-shrink-0 mt-0.5">
                        <XIcon />
                      </span>
                      <p className="text-sm text-gray-400">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Limitation of Liability */}
          {activeSection === 'liability' && (
            <div>
              <SectionTitle>
                {t('termsOfUse.sections.liability.title')}
              </SectionTitle>
              <div className="bg-red-500/10 border-l-4 border-red-500 rounded-r-lg p-4 mb-5">
                <p className="text-sm text-gray-300">
                  {t('termsOfUse.sections.liability.description')}
                </p>
              </div>
              <div className="feature-card">
                <h3 className="text-white font-semibold mb-3">
                  {t('termsOfUse.sections.liability.whatThisMeans')}
                </h3>
                <div className="space-y-3">
                  {[
                    t('termsOfUse.sections.liability.point1'),
                    t('termsOfUse.sections.liability.point2'),
                    t('termsOfUse.sections.liability.point3'),
                  ].map((text, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="bg-gray-500/20 p-1 rounded-full flex-shrink-0 mt-0.5">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </span>
                      <p className="text-sm text-gray-400">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Changes */}
          {activeSection === 'changes' && (
            <div>
              <SectionTitle>
                {t('termsOfUse.sections.changes.title')}
              </SectionTitle>
              <p className="text-gray-400 mb-4">
                {t('termsOfUse.sections.changes.description')}
              </p>
              <div className="feature-card flex items-start gap-4">
                <span className="bg-indigo-500/20 p-2 rounded-full flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-indigo-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
                <p className="text-sm text-gray-400">
                  {t('termsOfUse.sections.changes.note')}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </LegalPageLayout>
  );
};
